/**
 * `NewClientDialog` — the standalone "New Client" dialog (operator 2026-07-06,
 * "Both" placement: the case-navigator quick-create + the org-admin Clients tab).
 *
 * Creates a client via the gateway `client.create` (operator-token-fixed
 * 2026-07-06) — name + person/company. On success it invalidates the `["clients"]`
 * query so every client surface (navigator, reverse title bar, pickers) refreshes,
 * then navigates to the new client's detail page.
 *
 * Modal by design: a click outside does NOT close it — only Cancel / X / Esc.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, User, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogFooterCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useToastStore } from "@/stores/useToastStore";
import type { ClientCreateResult } from "@/types/gateway";
import { GatewayRpcError } from "@/lib/gateway-client";

interface NewClientDialogProps {
  onClose: () => void;
  /** Fired on a successful create (e.g. to seed a just-created client elsewhere). */
  onCreated?: (result: ClientCreateResult) => void;
  /** When false, stay on the current page after create (default true → navigate
   *  to the new client's detail page). */
  navigateOnCreate?: boolean;
}

/**
 * Turn a failed `client.create` into something a lawyer can act on.
 *
 * ⚖ **"You were told no" and "we could not ask" are DIFFERENT answers, and the
 * runtime deliberately keeps them apart** — since the D-87 org-mint gate went
 * live (roll #65, `dev-20260810.2301-955925398`), this RPC can return
 * `FORBIDDEN` (no permission to mint a client in this org) or `INTERNAL_ERROR`
 * (the gate could not be consulted — a DEPLOYMENT fault). Collapsing them into
 * one message sends an operator hunting for a missing grant that was never the
 * problem. Flagged by `lexruntime-changes` when they shipped the gate.
 *
 * ⚠ **Only `FORBIDDEN` gets bespoke copy.** `INTERNAL_ERROR` is a generic
 * gateway code with many possible causes, so mapping it to "the permission
 * check is unavailable" would be a closed map over an open vocabulary — the
 * false-green shape this codebase keeps getting bitten by. Instead, anything
 * that is not a refusal keeps the SERVER'S OWN WORDS as a second line, so the
 * unwired-gate text survives to whoever reads the screenshot, while the lawyer
 * still gets a sentence in their language first.
 *
 * ⚠ Matching on prose is NOT an option here: `GatewayRpcError` exists precisely
 * because branching on message text is unworkable (see its docblock).
 *
 * 📌 **A distinct code for the unwired-gate case is COMING, and this function is
 * shaped to take it.** `lexruntime-changes` accepted (2026-08-10) that they
 * encoded a real semantic distinction — *could not ask* vs *told no* — only in
 * prose, which makes it a comment rather than a contract, since no consumer can
 * branch on it. The vocabulary is `case-storage`'s ruled surface and has to
 * land consistently across all three create doors (`client.create`,
 * `matter.create`, and the case plane via `resolveParents`), so it rides the
 * D-104/D-105 batch. When that code arrives, add ONE branch beside `FORBIDDEN`.
 * Until then the detail-line fallback is the agreed answer — **do not "simplify"
 * it away, and do not invent a client-side code to stand in for it.**
 */
export function describeCreateFailure(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof GatewayRpcError && err.code === "FORBIDDEN") {
    return t("newClient.errors.forbidden");
  }
  const serverSaid = err instanceof Error && err.message ? err.message : "";
  return serverSaid
    ? `${t("newClient.errors.generic")} ${t("newClient.errors.detail", { detail: serverSaid })}`
    : t("newClient.errors.generic");
}

export function NewClientDialog({ onClose, onCreated, navigateOnCreate = true }: NewClientDialogProps) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const queryClient = useQueryClient();
  const selectClient = useActiveContextStore((s) => s.selectClient);
  const { identity } = useAuthIdentity();
  const addToast = useToastStore((s) => s.addToast);

  const [name, setName] = useState("");
  const [isPerson, setIsPerson] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canCreate = name.trim().length > 0 && !creating;

  const handleCreate = useCallback(async () => {
    if (!rpc || !name.trim() || creating) return;
    if (!identity?.userId) {
      setError(t("newClient.errors.noIdentity"));
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const result = await rpc.clientCreate({
        name: name.trim(),
        createdBy: identity.userId,
        isPerson,
      });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      onCreated?.(result);
      addToast({ text: t("newClient.toastCreated", { name: result.name }), kind: "success", autoDismissMs: 4000 });
      if (navigateOnCreate) {
        // N3: the detail page is retired — selecting the scope IS the
        // "open the new client" behaviour; the CASOS group appears via the
        // invalidation above.
        selectClient(result.clientId);
      }
      onClose();
    } catch (err) {
      setError(describeCreateFailure(err, t));
      setCreating(false);
    }
  }, [rpc, name, creating, identity, isPerson, queryClient, onCreated, addToast, navigateOnCreate, selectClient, onClose, t]);

  const inputCls = dialogInputCls;

  return (
    <div data-testid="new-client-dialog" className={dialogOverlayCls}>
      <div role="dialog" aria-modal="true" aria-label={t("newClient.title")} className={dialogPanelCls}>
        <div className={dialogHeaderCls}>
          <h2 className={cn(dialogTitleCls, "flex items-center gap-1.5")}>
            {isPerson ? <User size={16} className="text-cream-500" /> : <Users size={16} className="text-cream-500" />}
            {t("newClient.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            className={dialogCloseBtnCls}
          >
            <X size={16} />
          </button>
        </div>

        <label className={dialogLabelCls}>
          {t("newClient.nameLabel")}
        </label>
        <input
          ref={nameRef}
          data-testid="new-client-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleCreate();
            }
          }}
          placeholder={t("newClient.namePlaceholder")}
          maxLength={200}
          className={inputCls}
        />

        <label className="mt-3 flex items-center gap-1.5 text-body-sm text-coffee-700 dark:text-cream-300">
          <input
            type="checkbox"
            data-testid="new-client-isperson"
            checked={isPerson}
            onChange={(e) => setIsPerson(e.target.checked)}
            className="rounded border-cream-300"
          />
          {t("newClient.isPerson")}
        </label>

        {error && (
          <p
            data-testid="new-client-error"
            className="mt-3 rounded-lg bg-red-50 p-2.5 text-body-sm text-red-700 dark:bg-red-950 dark:text-red-400"
          >
            {error}
          </p>
        )}

        <div className={dialogFooterCls}>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className={dialogCancelBtnCls}
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            data-testid="new-client-submit"
            onClick={() => void handleCreate()}
            disabled={!canCreate}
            className={dialogPrimaryBtnCls(canCreate)}
          >
            {creating && <Loader2 size={13} className="animate-spin" />}
            {t("newClient.create")}
          </button>
        </div>
      </div>
    </div>
  );
}
